import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { sha256 } from 'js-sha256';

@Injectable({ providedIn: 'root' })
export class AuthService {
    public proxyUrl = environment.baseUrl;
    public _authenticated: boolean = false;
    private baseUrl = environment.baseUrl;

    //for seo
    public userEmailHash: string;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        @Inject(PLATFORM_ID) private platformId: any
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('accessToken', token);
        }
    }

    get accessToken(): string {
        if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined') {
          return localStorage.getItem('accessToken') ?? '';
        }
        return '';
      }
      


  
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {

        let headers = new HttpHeaders();
        headers = headers.append('Username', email);

        return this._httpClient.get(`${this.proxyUrl}/auth/forgotpassword`, { headers });
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string, token: string, email: string): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers.append('Token', token);
        headers = headers.append('Password', password);
        headers = headers.append('Username', email);

        return this._httpClient.get(`${this.proxyUrl}/auth/resetpassword`, { headers });
    }

    /**
     * Verify Email
     *
     * @param password
     */
    verifyEmailAddress(token: string): Observable<any> {

        return this._httpClient.get(`${this.proxyUrl}/service?url=endcustomer/verifyemailaddress?token=${token}`);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { Username: string; Password: string }): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers.append('Username', credentials.Username);
        headers = headers.append('Password', credentials.Password);
        headers = headers.append('AuthType', 'Customer');

        const options = {
            headers,
            responseType: 'text' as 'json'
        }
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._httpClient.get(`${this.proxyUrl}/auth/token`, options).pipe(
            switchMap((response: any) => {

                // Store the access token in the local storage
                this.accessToken = response;
 

                // Set the authenticated flag to true
                this._authenticated = true;
                this.userEmailHash = sha256(credentials.Username);
                // console.log(this.userEmailHash);

                // Store the user on the user service
                //this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            }),
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        return this._httpClient.post('api/auth/sign-in-with-token', {
            accessToken: this.accessToken,
        }).pipe(
            catchError(() =>

                // Return false
                of(false),
            ),
            switchMap((response: any) => {
                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                if (response.accessToken) {
                    this.accessToken = response.accessToken;
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;
                // console.log(this._userService.user);

                // Return true
                return of(true);
            }),
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        localStorage.removeItem('emailHash');
        localStorage.removeItem('phoneHash');


        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { FirstName: string; Surname: string; EmailAddress: string; Password: string; IDNumber: string; MobileNumber: string; AlternativeMobileNumber?: string }): Observable<any> {
        user.MobileNumber = '+27' + user.MobileNumber.replace('+27','')
        if (user.AlternativeMobileNumber) {
            user.AlternativeMobileNumber = '+27' + user.AlternativeMobileNumber.replace('+27','')
        }

        return this._httpClient.post(`${this.baseUrl}/auth/register`, user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    /**
     * Check the authentication status
     */
    accountAvailability(EmailAddress: string, IDNumber: number, MobileUsername: number) {
        const data = {
            Data: {
                EmailAddress,
                IDNumber,
                MobileUsername
            },
            Url: 'endcustomer/validateavailability'
        }

        return this._httpClient.post(`${this.proxyUrl}/service`, data);
    }

    /**
     * Register MobileUsername and send OTP
     */
    registerUsernameAndSendOTP(EmailAddress: string,) {
        const data = {
            Data: {
                EmailAddress,
            },
            Url: 'endcustomer/validateavailability_generateLoginOTP'
        }

        return this._httpClient.post(`${this.proxyUrl}/service`, data);
    }

    /**
     * Request OTP to mobile number
     */
    requestMobileOTP(MobileUsername: any) {
        let headers = new HttpHeaders();
        headers = headers.append('MobileUsername', MobileUsername);


        return this._httpClient.get(`${this.proxyUrl}/auth/requestMobileOTP`, { headers });
    }

    /**
     * Login using mobile OTP
     */
    loginWithOTP(MobileUsername: any, OTP: any) {
        let headers = new HttpHeaders();
        headers = headers.append('MobileUsername', MobileUsername);
        headers = headers.append('MobileOTP', OTP);

        return this._httpClient.get(`${this.proxyUrl}/auth/token/mobile_otp`, { headers,  responseType: 'text'});
    }

    /**
     * Sign up using mobile OTP
     */
    requestMobileOTPWithEmail(MobileUsername: any, FirstName: any) {
        let headers = new HttpHeaders();
        headers = headers.append('MobileUsername', MobileUsername);
        headers = headers.append('Name', FirstName);

        return this._httpClient.get(`${this.proxyUrl}/auth/mobileVerifyRequestOTP?isEmail=true`, { headers });
    }



}
